// Подготовка HTML из буфера обмена до разбора DOM: распознать программу-источник, вынуть таблицу стилей Word
// (класс -> имя стиля), заменить формулы Word (OMML в условных комментариях msEquation) метками, убрать запасные
// картинки формул, раскрыть VML-картинки, выбросить служебные условные блоки Office.
// Браузер сам не понимает «<![if …]>» Word - поэтому эта работа делается над строкой.

export type SourceApp = 'word' | 'libre' | 'gdocs' | 'excel' | 'web' | 'editor' | 'markdown' | 'text';

export const APP_LABEL: Record<SourceApp, string> = {
  word: 'из Word', libre: 'из LibreOffice', gdocs: 'из Google Документов', excel: 'из Excel', web: 'с веб-страницы',
  editor: 'из редактора', markdown: 'из Markdown', text: 'текста',
};

export interface StyleInfo {
  name: string;          // имя стиля Word («Заголовок 1», «Подпись рисунка»…) в нижнем регистре
  outline?: number;      // mso-outline-level (1..9)
  mono?: boolean;        // моноширинный шрифт стиля
  bold?: boolean;
}

export interface Prepared {
  html: string;
  app: SourceApp;
  /** фрагменты OMML Word (индекс = data-sto-omml) */
  omml: string[];
  /** класс абзаца -> стиль (из <style> документа Word) */
  styles: Map<string, StyleInfo>;
  /** убрано примечаний рецензента Word */
  commentsRemoved?: number;
}

// шрифт Symbol: латинская буква -> греческая / знак (Word пишет «α» как «a» шрифтом Symbol)
const SYMBOL_MAP: Record<string, string> = {
  a: 'α', b: 'β', g: 'γ', d: 'δ', e: 'ε', z: 'ζ', h: 'η', q: 'θ', i: 'ι', k: 'κ', l: 'λ', m: 'μ', n: 'ν', x: 'ξ', o: 'ο',
  p: 'π', r: 'ρ', s: 'σ', t: 'τ', u: 'υ', f: 'φ', c: 'χ', y: 'ψ', w: 'ω', j: 'ϕ', v: 'ϖ', J: 'ϑ',
  A: 'Α', B: 'Β', G: 'Γ', D: 'Δ', E: 'Ε', Z: 'Ζ', H: 'Η', Q: 'Θ', I: 'Ι', K: 'Κ', L: 'Λ', M: 'Μ', N: 'Ν', X: 'Ξ', O: 'Ο',
  P: 'Π', R: 'Ρ', S: 'Σ', T: 'Τ', U: 'Υ', F: 'Φ', C: 'Χ', Y: 'Ψ', W: 'Ω',
  '\u00b4': '×', '\u00b8': '÷', '\u00a3': '≤', '\u00b3': '≥', '\u00b9': '≠', '\u00bb': '≈', '\u00ae': '→', '\u00ac': '←',
  '\u00a5': '∞', '\u00b6': '∂', '\u00d6': '√', '\u00d1': '∇', '\u00ce': '∈', '\u00e5': '∑', '\u00f2': '∫', '\u00d7': '·',
  '\u00b0': '°', '\u00b1': '±', '\u00a2': '′', '\u00b2': '″', '\u00ba': '≡', '\u00de': '⇒', '\u00db': '⇔',
  '\uf061': 'α', '\uf062': 'β', '\uf067': 'γ', '\uf064': 'δ', '\uf065': 'ε', '\uf06c': 'λ', '\uf06d': 'μ', '\uf070': 'π',
  '\uf077': 'ω', '\uf06a': 'ϕ', '\uf066': 'φ', '\uf074': 'τ', '\uf073': 'σ', '\uf072': 'ρ', '\uf044': 'Δ', '\uf057': 'Ω',
  '\uf0b1': '±', '\uf0b4': '×', '\uf0a3': '≤', '\uf0b3': '≥', '\uf0b9': '≠', '\uf0bb': '≈', '\uf0ae': '→', '\uf0a5': '∞',
};

/** Чистка на уровне DOM: примечания рецензента Word, символы шрифта Symbol, оформление ссылок. */
function cleanDom(html: string, app: SourceApp): { html: string; comments: number } {
  if (typeof DOMParser === 'undefined') return { html, comments: 0 };
  const doc = new DOMParser().parseFromString('<body>' + html + '</body>', 'text/html');
  const b = doc.body;
  let comments = 0;
  // примечания рецензента Word: список примечаний в конце и якоря [A1] в тексте
  b.querySelectorAll('[style*="mso-element:comment-list"], [style*="mso-element: comment-list"]').forEach((el) => {
    comments += el.querySelectorAll('[style*="mso-element:comment"]:not([style*="comment-list"]), .msocomtxt').length || 1;
    el.remove();
  });
  b.querySelectorAll('.msocomtxt, [style*="mso-element:comment"], a.msocomanchor, .MsoCommentReference, .msocomoff, [style*="mso-special-character:comment"]')
    .forEach((el) => el.remove());
  // шрифт Symbol -> Unicode
  b.querySelectorAll('[style*="Symbol"], font[face="Symbol"]').forEach((el) => {
    const st = (el.getAttribute('style') || '') + ' ' + (el.getAttribute('face') || '');
    if (!/font-family:\s*"?Symbol|symbol-font-family:\s*"?Symbol|^\s*Symbol/i.test(st) && el.getAttribute('face') !== 'Symbol') return;
    const walker = doc.createTreeWalker(el, NodeFilter.SHOW_TEXT);
    const nodes: Text[] = [];
    while (walker.nextNode()) nodes.push(walker.currentNode as Text);
    for (const t of nodes) t.data = Array.from(t.data).map((ch) => SYMBOL_MAP[ch] ?? ch).join('');
  });
  // ссылки: подчёркивание и цвет - оформление ссылки, а не текста (если ссылка потом станет текстом - не остаются)
  b.querySelectorAll('a [style]').forEach((el) => {
    const st = (el.getAttribute('style') || '').replace(/text-decoration(-line)?\s*:[^;]*;?/gi, '').replace(/(^|;)\s*color\s*:[^;]*;?/gi, '$1');
    el.setAttribute('style', st);
  });
  // Google Документы: внутренняя ссылка на заголовок документа (#heading=…) - просто текст
  if (app === 'gdocs') b.querySelectorAll('a[href*="#heading="], a[href*="#bookmark="]').forEach((a) => a.replaceWith(...Array.from(a.childNodes)));
  return { html: b.innerHTML, comments };
}

export function detectApp(html: string): SourceApp {
  if (/data-pm-slice/.test(html)) return 'editor';
  if (/urn:schemas-microsoft-com:office:excel|ProgId"?\s+content="?Excel/i.test(html)) return 'excel';
  if (/urn:schemas-microsoft-com:office:word|ProgId"?\s+content="?Word|class="?Mso|mso-(list|style|bidi|spacerun|fareast)/i.test(html)) return 'word';
  if (/docs-internal-guid|id="?docs-internal/i.test(html)) return 'gdocs';
  if (/content="?LibreOffice|class="?(western|sdfootnote|cjk|ctl)\b|sdfootnoteanc|sdendnoteanc/i.test(html)) return 'libre';
  return 'web';
}

const MONO_RE = /courier|consolas|monaco|menlo|lucida console|monospace|source code|fira code|jetbrains mono|cascadia/i;

/** Правила <style> Word: «p.a3, li.a3, div.a3 {mso-style-name:"Подпись рисунка"; …}». */
function parseStyles(html: string): Map<string, StyleInfo> {
  const map = new Map<string, StyleInfo>();
  const blocks = html.match(/<style[^>]*>[\s\S]*?<\/style>/gi) || [];
  for (const b of blocks) {
    const css = b.replace(/<\/?style[^>]*>/gi, '').replace(/<!--|-->/g, '').replace(/\/\*[\s\S]*?\*\//g, '');
    const re = /([^{}]+)\{([^}]*)\}/g;
    let m: RegExpExecArray | null;
    while ((m = re.exec(css))) {
      const sel = m[1];
      const body = m[2];
      const nm = body.match(/mso-style-name:\s*("?)([^;]*?)\1\s*(;|$)/i);
      const classes = Array.from(sel.matchAll(/(?:^|[\s,])(?:p|h\d|li|div|span)\.([\w-]+)/gi)).map((x) => x[1]);
      if (!classes.length) continue;
      const ol = body.match(/mso-outline-level:\s*(\d)/i);
      const ff = body.match(/(?:^|;)\s*font-family:\s*([^;]+)/i);
      const info: StyleInfo = {
        name: (nm ? nm[2] : classes[0]).replace(/\\,/g, ',').replace(/\\/g, '').trim().toLowerCase(),
        outline: ol ? Number(ol[1]) : undefined,
        mono: ff ? MONO_RE.test(ff[1]) : undefined,
        bold: /font-weight:\s*(bold|[6-9]00)/i.test(body) || undefined,
      };
      for (const c of classes) if (!map.has(c)) map.set(c, info);
    }
  }
  return map;
}

/** Условные блоки «<![if X]> … <![endif]>» (видимые браузеру) с учётом вложенности: drop(X) - выбросить содержимое. */
function revealConditionals(html: string, drop: (cond: string) => boolean): string {
  const re = /<!\[if ([^\]]*)\]>|<!\[endif\]>/gi;
  let out = '';
  let last = 0;
  const stack: { cond: string; dropping: boolean }[] = [];
  let m: RegExpExecArray | null;
  const dropping = () => stack.some((s) => s.dropping);
  while ((m = re.exec(html))) {
    if (!dropping()) out += html.slice(last, m.index);
    last = m.index + m[0].length;
    if (m[1] !== undefined) stack.push({ cond: m[1].trim(), dropping: drop(m[1].trim()) });
    else stack.pop();
  }
  if (!dropping()) out += html.slice(last);
  return out;
}

/** VML-картинка из скрытого комментария -> <img> (если за комментарием нет запасного <img>). */
function vmlToImg(comment: string): string {
  const src = comment.match(/<v:imagedata[^>]*\bsrc="([^"]+)"/i);
  if (!src) return '';
  const st = comment.match(/<v:shape\b[^>]*style='([^']*)'/i) || comment.match(/<v:shape\b[^>]*style="([^"]*)"/i);
  const pt = (k: string) => {
    const mm = st?.[1].match(new RegExp(k + ':\\s*([\\d.]+)(pt|in|px)', 'i'));
    if (!mm) return '';
    const v = Number(mm[1]) * (mm[2] === 'in' ? 96 : mm[2] === 'pt' ? 96 / 72 : 1);
    return String(Math.round(v));
  };
  const w = pt('width');
  const h = pt('height');
  return `<img src="${src[1]}"${w ? ` width="${w}"` : ''}${h ? ` height="${h}"` : ''} data-vml="1">`;
}

export function prepHtml(raw: string): Prepared {
  const app = detectApp(raw);
  const styles = app === 'word' || app === 'excel' ? parseStyles(raw) : new Map<string, StyleInfo>();
  let html = raw;
  // фрагмент буфера Windows (CF_HTML): только между метками, но <style> уже разобран
  const sf = html.indexOf('<!--StartFragment-->');
  const ef = html.lastIndexOf('<!--EndFragment-->');
  if (sf >= 0 && ef > sf) html = html.slice(sf + 20, ef);
  const omml: string[] = [];
  // формулы Word: OMML -> метка; запасная картинка формулы ниже выбрасывается
  html = html.replace(/<!--\[if gte msEquation 12\]>([\s\S]*?)<!\[endif\]-->/gi, (_m, body: string) => {
    omml.push(body);
    const disp = /^\s*<m:oMathPara/i.test(body) ? ' data-display="1"' : '';
    return `<span data-sto-omml="${omml.length - 1}"${disp}></span>`;
  });
  // объекты OLE Word: ShapeID -> ProgID (Equation.3, Excel.Sheet…)
  const oleProg = new Map<string, string>();
  for (const m of html.matchAll(/<o:OLEObject\b[^>]*>/gi)) {
    const pg = (m[0].match(/ProgID="([^"]+)"/i) || [])[1];
    const sh = (m[0].match(/ShapeID="([^"]+)"/i) || [])[1];
    if (pg && sh) oleProg.set(sh, pg);
  }
  // VML: если за комментарием идёт <![if !vml]> с <img>, комментарий не нужен; иначе - вынуть картинку
  html = html.replace(/<!--\[if gte vml 1\]>([\s\S]*?)<!\[endif\]-->(\s*<!\[if !vml\]>)?/gi,
    (_m, body: string, fallback?: string) => {
      // формула Equation 3.0 / MathType: в буфере только картинка - место под формулу, картинка не переносится
      // (ProgID - в соседнем <o:OLEObject ShapeID=…>, у фигуры - атрибут o:ole)
      const sid = (body.match(/<v:shape\b[^>]*\bid="([^"]+)"/i) || [])[1] || '';
      const eq = /ProgID="?(Equation|MathType)/i.test(body) || (/\so:ole=/i.test(body) && /^(Equation|MathType)/i.test(oleProg.get(sid) || ''));
      if (eq) return '<span data-ole-eq="1"></span>' + (fallback ? '<![if !vmlOleEq]>' : '');
      return fallback ? fallback : vmlToImg(body);
    });
  // прочие скрытые условные комментарии Office (<xml>, настройки) и обычные комментарии
  html = html.replace(/<!--\[if[\s\S]*?<!\[endif\]-->/gi, '').replace(/<!--[\s\S]*?-->/g, '');
  // видимые условные блоки: запасные картинки формул и «выравнивающие» строки таблиц - выбросить
  html = revealConditionals(html, (c) => /^!msEquation$|^!supportMisalignedColumns$|^!supportAnnotations$|^!vmlOleEq$/i.test(c));
  html = html
    .replace(/<xml[\s\S]*?<\/xml>/gi, '')
    .replace(/<style[\s\S]*?<\/style>/gi, '')
    .replace(/<script[\s\S]*?<\/script>/gi, '')
    .replace(/<\/?(meta|link|title|base)\b[^>]*>/gi, '');
  const cd = cleanDom(html, app);
  return { html: cd.html, app, omml, styles, commentsRemoved: cd.comments };
}
